import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { AppShell } from '@/components/layout/AppShell'
import { DashboardSkeleton, HistorySkeleton, ReceiveSkeleton, SendSkeleton, SettingsSkeleton } from '@/components/ui/Skeletons'

// ─── Route-Level Code Splitting ─────────────────────────────────────────────────
// Each page is lazily loaded only when navigated to, reducing initial bundle size.

const Landing = lazy(() => import('@/pages/Landing').then(m => ({ default: m.Landing })))
const Login = lazy(() => import('@/pages/Login').then(m => ({ default: m.Login })))
const CreateWallet = lazy(() => import('@/pages/CreateWallet').then(m => ({ default: m.CreateWallet })))
const Dashboard = lazy(() => import('@/pages/Dashboard').then(m => ({ default: m.Dashboard })))
const SendPage = lazy(() => import('@/pages/Send').then(m => ({ default: m.SendPage })))
const Receive = lazy(() => import('@/pages/Receive').then(m => ({ default: m.Receive })))
const History = lazy(() => import('@/pages/History').then(m => ({ default: m.History })))
const Settings = lazy(() => import('@/pages/Settings').then(m => ({ default: m.Settings })))
const WellsWallet = lazy(() => import('@/pages/WellsWallet').then(m => ({ default: m.WellsWallet })))
const Help = lazy(() => import('@/pages/Help').then(m => ({ default: m.Help })))
const NotFound = lazy(() => import('@/pages/NotFound').then(m => ({ default: m.NotFound })))

// ─── Auth Splash Screen ─────────────────────────────────────────────────────────
// A premium full-screen loader shown while auth initializes. This replaces the
// old generic LoadingSpinner and provides a branded experience.

function AuthSplash() {
    return (
        <div className="min-h-screen bg-[#0a0a0f] flex flex-col items-center justify-center relative overflow-hidden">
            {/* Ambient glow */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-indigo-500/15 to-purple-600/10 rounded-full blur-3xl" />
            </div>

            {/* Logo + Loading */}
            <div className="relative z-10 flex flex-col items-center gap-6 animate-fadeIn">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-2xl shadow-purple-500/30">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
                        <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
                        <path d="M18 12a2 2 0 0 0 0 4h4v-4Z" />
                    </svg>
                </div>

                <div className="text-center">
                    <h1 className="text-white text-xl font-bold tracking-tight">Wells Wallet</h1>
                    <p className="text-white/40 text-sm mt-1">Initializing secure session…</p>
                </div>

                {/* Animated progress bar */}
                <div className="w-48 h-1 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full animate-pulse"
                        style={{ width: '60%', animation: 'authProgress 1.5s ease-in-out infinite' }}
                    />
                </div>
            </div>
        </div>
    )
}

// ─── Route Guards ───────────────────────────────────────────────────────────────

function ProtectedRoute({ children }: { children: React.ReactNode }) {
    const { isAuthenticated, isLoading } = useAuth()

    if (isLoading) {
        return <AuthSplash />
    }

    if (!isAuthenticated) {
        return <Navigate to="/landing" replace />
    }

    return <>{children}</>
}

function PublicRoute({ children }: { children: React.ReactNode }) {
    const { isAuthenticated, isLoading } = useAuth()

    if (isLoading) {
        return <AuthSplash />
    }

    if (isAuthenticated) {
        return <Navigate to="/" replace />
    }

    return <>{children}</>
}

// ─── Fallbacks ──────────────────────────────────────────────────────────────────
// Page-specific skeleton fallbacks for Suspense — so users see the correct
// skeleton layout while the lazy chunk downloads, not a generic spinner.

function DashboardFallback() {
    return <AppShell><DashboardSkeleton /></AppShell>
}
function HistoryFallback() {
    return <AppShell><HistorySkeleton /></AppShell>
}
function ReceiveFallback() {
    return <AppShell><ReceiveSkeleton /></AppShell>
}
function SendFallback() {
    return <AppShell><SendSkeleton /></AppShell>
}
function SettingsFallback() {
    return <AppShell><SettingsSkeleton /></AppShell>
}
function MinimalFallback() {
    return <div className="min-h-screen bg-[#0a0a0f]" />
}

// ─── App ────────────────────────────────────────────────────────────────────────

function App() {
    return (
        <div className="min-h-screen bg-wallet-bg text-white">
            <Routes>
                {/* Public Routes — no AppShell */}
                <Route
                    path="/landing"
                    element={
                        <PublicRoute>
                            <Suspense fallback={<MinimalFallback />}>
                                <Landing />
                            </Suspense>
                        </PublicRoute>
                    }
                />
                <Route
                    path="/login"
                    element={
                        <PublicRoute>
                            <Suspense fallback={<MinimalFallback />}>
                                <Login />
                            </Suspense>
                        </PublicRoute>
                    }
                />
                <Route
                    path="/wellswallet"
                    element={
                        <Suspense fallback={<MinimalFallback />}>
                            <WellsWallet />
                        </Suspense>
                    }
                />
                <Route
                    path="/help"
                    element={
                        <Suspense fallback={<MinimalFallback />}>
                            <Help />
                        </Suspense>
                    }
                />

                {/* Protected Routes — wrapped in AppShell with page-specific skeleton fallbacks */}
                <Route
                    path="/"
                    element={
                        <ProtectedRoute>
                            <Suspense fallback={<DashboardFallback />}>
                                <AppShell>
                                    <Dashboard />
                                </AppShell>
                            </Suspense>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/create-wallet"
                    element={
                        <ProtectedRoute>
                            <Suspense fallback={<MinimalFallback />}>
                                <CreateWallet />
                            </Suspense>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/send"
                    element={
                        <ProtectedRoute>
                            <Suspense fallback={<SendFallback />}>
                                <AppShell>
                                    <SendPage />
                                </AppShell>
                            </Suspense>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/receive"
                    element={
                        <ProtectedRoute>
                            <Suspense fallback={<ReceiveFallback />}>
                                <AppShell>
                                    <Receive />
                                </AppShell>
                            </Suspense>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/history"
                    element={
                        <ProtectedRoute>
                            <Suspense fallback={<HistoryFallback />}>
                                <AppShell>
                                    <History />
                                </AppShell>
                            </Suspense>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/settings"
                    element={
                        <ProtectedRoute>
                            <Suspense fallback={<SettingsFallback />}>
                                <AppShell>
                                    <Settings />
                                </AppShell>
                            </Suspense>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/exchange"
                    element={
                        <ProtectedRoute>
                            <Suspense fallback={<DashboardFallback />}>
                                <AppShell>
                                    <Dashboard />
                                </AppShell>
                            </Suspense>
                        </ProtectedRoute>
                    }
                />

                {/* Fallback - 404 Page */}
                <Route
                    path="*"
                    element={
                        <Suspense fallback={<MinimalFallback />}>
                            <NotFound />
                        </Suspense>
                    }
                />
            </Routes>
        </div>
    )
}

export default App
